import client from "services/ngage/api-client";
import {TrailRating, TrailRatingData} from "types/TrailRating";
import deserializeTrailRating from "services/ngage/deserializers/trail-rating";
import DataResource from "types/DataResource";
import {snakeKeys} from "services/array";

type TrailRatingFilters = {
    search?: string | null
    page?: number
    perPage?: number
}

/**
 * Get all trail ratings that match the given filters (paginated)
 */
const filtered = async (filters: TrailRatingFilters = {}) => {
    const params = Object.assign({page: 1, per_page: 100}, snakeKeys(filters))

    try {
        const page = (await client.get<DataResource<TrailRating> | null>('/trail-ratings', {params})).data

        if (page) page.data = page.data.map(deserializeTrailRating)

        return page
    } catch (error) {
        console.error("Unable to fetch trail ratings", error)
        return null
    }
}

/**
 * Get a single trail rating
 *
 * @param {number} trailRatingId
 */
const get = async (trailRatingId: number) => {
    try {
        const trailRating = (await client.get<TrailRating | null>(`/trail-ratings/${trailRatingId}`)).data
        return trailRating ? deserializeTrailRating(trailRating) : null
    } catch (error) {
        console.error(`Unable to fetch trail rating ${trailRatingId}`, error)
        return null
    }
}

/**
 * Store a new trail rating
 *
 * @param data
 */
const store = async (data: TrailRatingData) => {
    try {
        const trailRating = (await client.post<TrailRating | null>('/trail-ratings', data)).data
        return trailRating ? deserializeTrailRating(trailRating) : null
    } catch (error) {
        console.error("Unable to store trail rating", error)
        throw error
    }
}

/**
 * Update an existing trail rating
 *
 * @param {number} trailRatingId
 * @param data
 */
const update = async (trailRatingId: number, data: TrailRatingData) => {
    try {
        const trailRating = (await client.put<TrailRating | null>(`/trail-ratings/${trailRatingId}`, data)).data
        return trailRating ? deserializeTrailRating(trailRating) : null
    } catch (error) {
        console.error(`Unable to update trail rating ${trailRatingId}`, error)
        throw error
    }
}

/**
 * Permanently delete a trail rating
 *
 * @param {number} trailRatingId
 */
const destroy = async (trailRatingId: number) => {
    try {
        return (await client.delete<null>(`/trail-ratings/${trailRatingId}`)).status === 204
    } catch (error) {
        console.error(`Unable to delete trail rating ${trailRatingId}`, error)
        throw error
    }
}

const trailRatings = {
    filtered,
    get,
    store,
    update,
    destroy
}

export default trailRatings;